import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, X } from "lucide-react";

/**
 * Voice input button using the browser Web Speech API (SpeechRecognition).
 * Optional, accessible fallback: if unsupported, button is hidden.
 *
 * Props:
 *   - value: current textarea value
 *   - onChange: (text) => void  // called with appended/replaced transcribed text
 *   - mode: "append" | "replace"  (default "append")
 */
export default function VoiceInput({ value = "", onChange, mode = "append" }) {
    const [supported, setSupported] = useState(false);
    const [listening, setListening] = useState(false);
    const [interimText, setInterimText] = useState("");
    const [error, setError] = useState("");
    const recognitionRef = useRef(null);
    // Text already in the textarea when the user pressed the mic — the voice
    // session only ever appends to this.
    const baseTextRef = useRef("");
    // Map of "absolute SR result index" -> final transcript. Indexing by
    // position guarantees each utterance can only contribute once even if the
    // browser re-emits earlier final results in later events (a known
    // SpeechRecognition quirk that produced word doubling).
    const finalsRef = useRef({});

    useEffect(() => {
        const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
        if (!SR) return;
        setSupported(true);
        const r = new SR();
        r.lang = "en-US";
        r.interimResults = true;
        r.continuous = true;
        r.maxAlternatives = 1;

        const buildOutput = (interim) => {
            const finals = Object.keys(finalsRef.current)
                .map((k) => Number(k))
                .sort((a, b) => a - b)
                .map((k) => finalsRef.current[k])
                .join(" ");
            // Compose: baseText (what was typed before mic), then final
            // transcripts, then current interim. Single-space separated, with
            // collapsed whitespace and no space before punctuation.
            const out = [baseTextRef.current, finals, interim]
                .map((s) => (s || "").trim())
                .filter(Boolean)
                .join(" ")
                .replace(/\s+([.,!?;:])/g, "$1")
                .replace(/\s+/g, " ");
            return out;
        };

        r.onresult = (event) => {
            let interim = "";
            for (let i = 0; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalsRef.current[i] = transcript.trim();
                } else {
                    interim += transcript;
                }
            }
            setInterimText(interim);
            onChange(buildOutput(interim));
        };
        r.onerror = (e) => {
            const msg = e.error === "not-allowed"
                ? "Microphone access denied. Allow mic permission to use voice input."
                : e.error === "no-speech"
                ? "No speech detected — try again."
                : `Voice error: ${e.error}`;
            setError(msg);
            setListening(false);
        };
        r.onend = () => {
            setListening(false);
            setInterimText("");
        };
        recognitionRef.current = r;
        return () => { try { r.abort(); } catch { /* ignore */ } };
    }, []); // eslint-disable-line

    const start = () => {
        const r = recognitionRef.current;
        if (!r) return;
        setError("");
        // Snapshot current text and reset the per-session final map so we
        // start fresh — the next utterances will be appended to this base.
        baseTextRef.current = mode === "replace" ? "" : (value || "").trim();
        finalsRef.current = {};
        setInterimText("");
        try {
            r.start();
            setListening(true);
        } catch (e) {
            setError("Voice recognition already running.");
        }
    };

    const stop = () => {
        const r = recognitionRef.current;
        if (!r) return;
        try { r.stop(); } catch { /* ignore */ }
    };

    if (!supported) {
        return (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="voice-unsupported">
                <MicOff className="h-3.5 w-3.5" /> Voice input not supported in this browser
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2" data-testid="voice-input-wrapper">
            <button
                type="button"
                onClick={listening ? stop : start}
                aria-pressed={listening}
                aria-label={listening ? "Stop voice input" : "Start voice input"}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                    listening
                        ? "bg-destructive text-destructive-foreground border-destructive animate-pulse"
                        : "bg-card border-border hover:bg-accent text-foreground"
                }`}
                data-testid="voice-toggle-btn">
                {listening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                {listening ? "Listening — tap to stop" : "Speak instead"}
            </button>
            {value && !listening && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                    data-testid="voice-clear-btn"
                    aria-label="Clear text">
                    <X className="h-3 w-3" /> Clear
                </button>
            )}
            {listening && interimText && (
                <span className="text-[11px] text-muted-foreground italic truncate max-w-[160px]" data-testid="voice-interim">
                    "{interimText}"
                </span>
            )}
            {error && (
                <span className="text-[11px] text-destructive" data-testid="voice-error">{error}</span>
            )}
        </div>
    );
}
